import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Header, Icon } from 'semantic-ui-react'
import ProfileSettingsService from '~/api/services/profileSettings'
import CustomIcon from '~/components/CustomIcon'
import { FormFields } from '~/components/FormFields'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { passwordRegex, MINIMUM_LENGTH_PASSWORD, MAX_LENGTH_PASSWORD, passwordRegexWithoutSpecChar } from '~/Constants'
import { handleErrors } from '~/utils/helpers'

interface ChangePasswordProps {
  passwordSecurity: number | boolean
}

const ChangePassword: React.FC<ChangePasswordProps> = (props) => {
  const { passwordSecurity } = props
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const [passwordFieldVisible, setPasswordFieldVisible] = useState({
    password: false,
    passwordCurrent: false,
    passwordRepeat: false
  })

  const { handleSubmit, control, getValues, reset } = useForm({
    mode: 'onSubmit'
  })
  const matchPasswordErrorMessage = t('Passwords do not match')

  const setVisibleFieldPassword = (field) => {
    setPasswordFieldVisible({
      ...passwordFieldVisible,
      [field]: !passwordFieldVisible[field]
    })
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      await ProfileSettingsService.updatePassword(data)
      notify(t('Password updated'), 'success')
      reset({
        currentPassword: '',
        password: '',
        passwordConfirmation: ''
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderChangePasswordForm() {
    return <>
      <FormFields.Input
        control={control}
        name='currentPassword'
        placeholder={t('Current password')}
        label={t('Current password')}
        width={5}
        type={passwordFieldVisible.passwordCurrent ? 'text' : 'password'}
        icon={
          <CustomIcon
            link
            size='large'
            style={{ opacity: 1, cursor: 'pointer' }}
            name='eye'
            onMouseDown={() => setVisibleFieldPassword('passwordCurrent')}
            onMouseUp={() => setVisibleFieldPassword('passwordCurrent')}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter password')
          },
          pattern: {
            value: passwordRegexWithoutSpecChar,
            message: t('The password must contain at least one uppercase, lowercase letter, and at least one symbol')
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='password'
        label={t('New password')}
        placeholder={t('New password')}
        width={5}
        type={passwordFieldVisible.password ? 'text' : 'password'}
        icon={
          <CustomIcon
            size='large'
            link
            style={{ opacity: 1, cursor: 'pointer' }}
            name='eye'
            onMouseDown={() => setVisibleFieldPassword('password')}
            onMouseUp={() => setVisibleFieldPassword('password')}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter password')
          },
          pattern: {
            value: passwordSecurity ? passwordRegex : passwordRegexWithoutSpecChar,
            message: t('The password must contain at least one uppercase, lowercase letter, and at least one symbol')
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='passwordConfirmation'
        label={t('Confirm new password')}
        width={5}
        placeholder={t('Confirm new password')}
        type={passwordFieldVisible.passwordRepeat ? 'text' : 'password'}
        icon={
          <CustomIcon
            size='large'
            link
            style={{ opacity: 1, cursor: 'pointer' }}
            name='eye'
            onMouseDown={() => setVisibleFieldPassword('passwordRepeat')}
            onMouseUp={() => setVisibleFieldPassword('passwordRepeat')}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter password confirmation')
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 6 characters long')
          },
          validate: value => getValues('password') === value || matchPasswordErrorMessage
        }}
      />
    </>
  }

  function renderHeader() {
    return <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline' }}>
        <Icon name='lock' size='large' color='blue' />
        <Header as={'h4'}>{t('Change password')}</Header>
      </div>
      <El.PageHeaderActionButton>{t('Change')}</El.PageHeaderActionButton>
    </div>
  }

  return <Form loading={isBusy} style={{ marginBottom: 20 }} onSubmit={handleSubmit(onSubmit)}>
    {renderHeader()}
    {renderChangePasswordForm()}
  </Form>

}
export default ChangePassword
